import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { BusinessService, BusinessStore } from '../../core/services/business.service';
import { CustomerProfile, Product } from '../../core/models/product.models';
import { CustomersService } from '../../core/services/customers.service';
import { OrdersService } from '../../core/services/orders.service';
import { ProductsService } from '../../core/services/products.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { CurrentUserService } from '../../core/auth/current-user.service';
import { FinanceService } from '../../core/services/finance.service';

interface Supplier {
  id: string;
  name: string;
  contactName?: string;
  phone?: string;
  email?: string;
  taxNumber?: string;
  address?: string;
}
interface PurchaseLine {
  productId?: string;
  categoryId?: string;
  brandId?: string;
  productSearch: string;
  isCreatingNew: boolean;
  inlineSaved: boolean;
  title: string;
  description: string;
  modelName: string;
  identifierType: '' | 'IMEI' | 'SerialNumber' | 'VehicleRegistration' | 'Other';
  identifierValue: string;
  condition: 'New' | 'Used';
  quantity: number | null;
  unitCost: number | null;
  repairCost: number | null;
  extraCost: number | null;
  thumbnail?: File;
  thumbnailUrl: string;
  galleryImages: File[];
  galleryUrls: string[];
  hasOriginalBill: boolean | null;
  invoiceNumber: string;
  invoices: EvidenceFile[];
  verificationPhotos: EvidenceFile[];
  missingBillReason: string;
  isSellerProductOwner: boolean;
  productOwnerName: string;
  productOwnerPhone: string;
  productOwnerAddress: string;
  productOwnerIdentityDocuments: EvidenceFile[];
}
interface EvidenceFile {
  file: File;
  url: string;
  isPdf: boolean;
}

@Component({
  selector: 'app-purchases',
  imports: [FormsModule],
  templateUrl: './purchases.html',
  styleUrls: ['./purchases.scss', './purchases-enhancements.scss', './purchases-media.scss'],
})
export class Purchases implements OnInit, OnDestroy {
  private readonly orders = inject(OrdersService);
  private readonly business = inject(BusinessService);
  private readonly customers = inject(CustomersService);
  private readonly productsService = inject(ProductsService);
  private readonly router = inject(Router);
  private readonly currentUser = inject(CurrentUserService);
  private readonly finance = inject(FinanceService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly productSearchRequests = new Subject<string>();
  private readonly destroyed = new Subject<void>();
  step: 'choose' | 'details' = 'choose';
  purchaseType: 'CompanyUse' | 'SupplierProcurement' | 'BusinessUsed' | 'IndividualUsed' | null =
    null;
  usedSellerType: 'Business' | 'Individual' | null = null;
  suppliers: Supplier[] = [];
  supplierSearch = '';
  isLoadingSuppliers = false;
  stores: BusinessStore[] = [];
  products: Product[] = [];
  savedPurchaseItems: PurchaseLine[] = [];
  supplierId = '';
  storeId = '';
  franchisePartners: Array<{id:string;name:string;available:number;principal:number;utilized:number;availablePrincipal?:number;retainedProfit?:number}>=[];
  franchiseAllocations: Array<{franchisePartnerId:string;amount:number|null;percentage:number|null}> = [];
  get totalFranchiseFunding(): number { return this.franchiseAllocations.reduce((sum,x)=>sum+(Number(x.amount)||0),0); }
  companyFundEnabled = true;
  companyFundBalance = 0;
  get businessFunding(): number { return this.companyFundEnabled ? Math.max(0, this.total-this.totalFranchiseFunding) : 0; }
  sellerInvoiceNumber = '';
  notes = '';
  discountAmount: number | null = null;
  taxAmount: number | null = null;
  transportAmount: number | null = null;
  otherCharges: number | null = null;
  amountPaid: number | null = null;
  paymentMethod = '';
  sellerInvoices: EvidenceFile[] = [];
  sellerIdentityDocuments: EvidenceFile[] = [];
  sellerVerificationPhotos: EvidenceFile[] = [];
  productOwnerIdentityDocuments: EvidenceFile[] = [];
  hasOriginalBill: boolean | null = null;
  missingBillReason = '';
  sellerAddress = '';
  isSellerProductOwner = false;
  productOwnerName = '';
  productOwnerPhone = '';
  productOwnerAddress = '';
  locationMessage = '';
  isLocationPickerOpen = false;
  pendingStoreId = '';
  individualPhone = '';
  aadhaarNumber = '';
  aadhaarOtp = '';
  aadhaarReferenceId = '';
  individualSeller?: CustomerProfile;
  isVerifying = false;
  productImages: File[] = [];
  productImageUrls: string[] = [];
  fullImageUrl = '';
  fullImageAlt = '';
  lines: PurchaseLine[] = [];
  draftLine: PurchaseLine = this.emptyLine();
  editingLineIndex: number | null = null;
  showSupplierResults = false;
  showProductResults = false;
  isSearchingProducts = false;
  showNewSupplier = false;
  supplierForm = { name: '', contactName: '', phone: '', email: '', taxNumber: '', address: '' };
  isSavingSupplier = false;
  supplierFeedback = '';
  supplierError = '';
  isSaving = false;
  errorMessage = '';
  successMessage = '';
  completedVoucherUrl = '';
  ngOnInit(): void {
    this.loadLocations();
    this.loadSuppliers();
    this.refreshProducts();
    this.productSearchRequests.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((search) => {
        this.isSearchingProducts = true;
        this.changeDetector.markForCheck();
        return forkJoin({
          products: this.productsService.list({ search, listingType: 'New', pageNumber: 1, pageSize: 10 }).pipe(catchError(() => of([]))),
          purchasedItems: this.orders.purchaseCatalogItems(search).pipe(catchError(() => of([]))),
        }).pipe(
          finalize(() => {
            this.isSearchingProducts = false;
            this.changeDetector.markForCheck();
          }),
        );
      }),
      takeUntil(this.destroyed),
    ).subscribe(({ products, purchasedItems }) => {
      const merged = new Map(this.products.map((product) => [product.id, product]));
      products.forEach((product) => merged.set(product.id, product));
      this.products = [...merged.values()];
      purchasedItems.forEach((item) => this.rememberPurchaseItem({
        ...this.emptyLine(),
        productSearch: `${item.title}${item.modelName ? ' · ' + item.modelName : ''}`,
        title: item.title,
        modelName: item.modelName ?? '',
        description: item.description ?? '',
        quantity: 1,
        unitCost: Number(item.unitCost) || null,
        extraCost: Number(item.extraCost) || null,
        inlineSaved: true,
      }));
      this.changeDetector.markForCheck();
    });
  }
  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
    this.revokePreviews();
  }
  choose(type: 'CompanyUse' | 'SupplierProcurement' | 'IndividualUsed'): void {
    this.purchaseType = type;
    this.usedSellerType = null;
    this.hasOriginalBill = type === 'SupplierProcurement' ? true : null;
    this.step = 'details';
    this.lines = [];
    this.draftLine = this.emptyLine();
    this.editingLineIndex = null;
    this.supplierId = '';
    this.supplierSearch = '';
    this.errorMessage = '';
    this.isSellerProductOwner = type !== 'IndividualUsed';
  }
  chooseUsedSeller(type: 'Business' | 'Individual'): void {
    this.usedSellerType = type;
    this.purchaseType = type === 'Business' ? 'BusinessUsed' : 'IndividualUsed';
    this.supplierId = '';
    this.supplierSearch = '';
    this.individualSeller = undefined;
    this.aadhaarReferenceId = '';
    this.aadhaarOtp = '';
    this.errorMessage = '';
    this.isSellerProductOwner = false;
    this.sellerAddress = '';
    this.lines = [];
    this.draftLine = this.emptyLine();
  }
  back(): void {
    this.step = 'choose';
    this.purchaseType = null;
    this.usedSellerType = null;
  }
  get isUsed(): boolean {
    return this.purchaseType === 'BusinessUsed' || this.purchaseType === 'IndividualUsed';
  }
  get isCompanyUse(): boolean {
    return this.purchaseType === 'CompanyUse';
  }
  get searchableNewProducts(): Product[] {
    return this.products.filter((product) => {
      const value = product.condition ?? product.listingType;
      return value === 'New' || value === 1;
    });
  }
  get filteredSuppliers(): Supplier[] {
    const query = this.supplierSearch.trim().toLocaleLowerCase();
    if (!query) return this.suppliers.slice(0, 8);
    return this.suppliers
      .filter((supplier) =>
        [supplier.name, supplier.contactName, supplier.phone, supplier.email]
          .filter(Boolean)
          .some((value) => String(value).toLocaleLowerCase().includes(query)),
      )
      .slice(0, 8);
  }
  filteredProducts(line: PurchaseLine): Product[] {
    const query = line.productSearch.trim().toLocaleLowerCase();
    return this.searchableNewProducts
      .filter((product) =>
        !query ||
        [product.title, product.modelName]
          .filter(Boolean)
          .some((value) => String(value).toLocaleLowerCase().includes(query)),
      )
      .slice(0, 8);
  }
  filteredSavedPurchaseItems(line: PurchaseLine): PurchaseLine[] {
    const query = line.productSearch.trim().toLocaleLowerCase();
    return this.savedPurchaseItems
      .filter((item) =>
        !query ||
        [item.title, item.modelName]
          .filter(Boolean)
          .some((value) => String(value).toLocaleLowerCase().includes(query)),
      )
      .slice(0, 5);
  }
  canUsePurchaseType(code: string): boolean {
    return this.currentUser.hasPermission(code);
  }
  get canPostPurchase(): boolean { return this.currentUser.hasPermission('PURCHASE_POST'); }
  get locationScope(): 'platform' | 'business' {
    return this.router.url.startsWith('/admin') ? 'platform' : 'business';
  }
  get selectedStore(): BusinessStore | undefined {
    return this.stores.find((x) => x.id === this.storeId);
  }
  get currentSellerName(): string {
    if (this.purchaseType === 'IndividualUsed') {
      return (
        this.individualSeller?.aadhaarVerifiedName ||
        `${this.individualSeller?.firstName ?? ''} ${this.individualSeller?.lastName ?? ''}`.trim()
      );
    }
    return this.suppliers.find((supplier) => supplier.id === this.supplierId)?.name ?? '';
  }
  get currentSellerPhone(): string {
    if (this.purchaseType === 'IndividualUsed') return this.individualSeller?.phoneNumber ?? '';
    return this.suppliers.find((supplier) => supplier.id === this.supplierId)?.phone ?? '';
  }
  get selectedStoreAddress(): string {
    const s = this.selectedStore;
    return s
      ? [s.addressLine2, s.addressLine1, s.area, s.city, s.state, s.postalCode, s.country]
          .filter(Boolean)
          .join(', ')
      : '';
  }
  get total(): number {
    const subtotal = this.lines.reduce(
      (sum, line) =>
        sum +
        ((Number(line.unitCost) || 0) +
          (Number(line.repairCost) || 0) +
          (Number(line.extraCost) || 0)) *
          (this.isUsed ? 1 : Number(line.quantity) || 1),
      0,
    );
    return Math.max(
      0,
      subtotal -
        (Number(this.discountAmount) || 0) +
        (Number(this.taxAmount) || 0) +
        (Number(this.transportAmount) || 0) +
        (Number(this.otherCharges) || 0),
    );
  }
  addLine(): void {
    this.lines = [...this.lines, this.emptyLine()];
  }
  removeLine(i: number): void {
    this.lines = this.lines.filter((_, n) => n !== i);
    if (this.editingLineIndex === i) this.cancelLineEdit();
  }
  selectExistingProduct(line: PurchaseLine, id: string): void {
    line.productId = id || undefined;
    const p = this.products.find((x) => x.id === id);
    if (p) {
      line.title = p.title;
      line.modelName = p.modelName ?? '';
      line.categoryId = p.categoryId ?? undefined;
      line.brandId = p.brandId ?? undefined;
      line.productSearch = `${p.title}${p.modelName ? ' · ' + p.modelName : ''}`;
      line.isCreatingNew = false;
      line.inlineSaved = false;
      this.showProductResults = false;
    } else {
      line.title = '';
      line.modelName = '';
    }
  }
  matchProduct(line: PurchaseLine): void {
    const search = line.productSearch.trim().toLowerCase();
    const product = this.searchableNewProducts.find(
      (x) => `${x.title}${x.modelName ? ' · ' + x.modelName : ''}`.toLowerCase() === search,
    );
    if (product) this.selectExistingProduct(line, product.id);
    else line.productId = undefined;
  }
  productSearchChanged(line: PurchaseLine): void {
    const search = line.productSearch.trim();
    const selected = line.productId
      ? this.products.find((product) => product.id === line.productId)
      : undefined;
    const selectedLabel = selected
      ? `${selected.title}${selected.modelName ? ' · ' + selected.modelName : ''}`
      : '';
    if (selectedLabel.toLocaleLowerCase() !== search.toLocaleLowerCase()) line.productId = undefined;
    if (line.inlineSaved && line.title.trim().toLocaleLowerCase() !== search.toLocaleLowerCase()) {
      line.inlineSaved = false;
    }
    this.showProductResults = true;
    if (search.length >= 2) this.productSearchRequests.next(search);
  }
  createNewLineItem(line: PurchaseLine): void {
    line.productId = undefined;
    line.isCreatingNew = true;
    line.title = line.productSearch.trim();
    line.modelName = '';
    line.description = '';
    line.condition = 'New';
    line.inlineSaved = false;
    this.showProductResults = false;
  }
  saveInlineItem(line: PurchaseLine): void {
    if (!line.title.trim()) {
      this.errorMessage = 'Enter the new product name.';
      return;
    }
    line.productSearch = line.title.trim();
    line.inlineSaved = true;
    line.isCreatingNew = false;
    this.rememberPurchaseItem(line);
    this.errorMessage = '';
    this.successMessage = 'New product details saved and added to product search.';
    this.changeDetector.detectChanges();
  }
  selectSavedPurchaseItem(line: PurchaseLine, saved: PurchaseLine): void {
    const quantity = line.quantity;
    const unitCost = line.unitCost;
    const extraCost = line.extraCost;
    Object.assign(line, this.cloneLine(saved), {
      quantity,
      unitCost,
      extraCost,
      productId: undefined,
      isCreatingNew: false,
      inlineSaved: true,
    });
    this.showProductResults = false;
    this.errorMessage = '';
  }
  addOrUpdatePurchaseLine(): void {
    const line = this.draftLine;
    this.errorMessage = '';
    if (!this.isUsed) {
      if (!line.productId) this.matchProduct(line);
      if (line.isCreatingNew && line.title.trim()) {
        line.productSearch = line.title.trim();
        line.inlineSaved = true;
        line.isCreatingNew = false;
        this.rememberPurchaseItem(line);
      }
    }
    if (!line.title.trim()) {
      this.errorMessage = this.isUsed ? 'Enter the used product title.' : 'Select a product or create and save a new product first.';
      return;
    }
    if (!this.isUsed && !line.productId && !line.inlineSaved) {
      this.errorMessage = 'Select a product from search, or choose Create new item and enter its name.';
      return;
    }
    if (!Number(line.quantity) || Number(line.quantity) <= 0) {
      this.errorMessage = 'Enter a valid quantity.';
      return;
    }
    if (!Number(line.unitCost) || Number(line.unitCost) <= 0) {
      this.errorMessage = 'Enter a valid purchase price.';
      return;
    }
    if (this.isUsed && line.galleryImages.length === 0 && !line.thumbnail) {
      this.errorMessage = 'Add at least one clear image for this used product.';
      return;
    }
    if (this.isUsed && Boolean(line.identifierType) !== Boolean(line.identifierValue.trim())) {
      this.errorMessage = 'Select the unique identifier type and enter its value, or leave both empty.';
      return;
    }
    if (line.identifierType === 'IMEI' && !/^\d{15}$/.test(line.identifierValue.trim())) {
      this.errorMessage = 'IMEI must contain exactly 15 digits.';
      return;
    }
    if (line.identifierType === 'VehicleRegistration') {
      line.identifierValue = line.identifierValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15);
      if (line.identifierValue.length < 6) {
        this.errorMessage = 'Enter a valid vehicle registration number.';
        return;
      }
    }
    if (this.isUsed && line.hasOriginalBill === null) {
      this.errorMessage = 'Select whether the original bill is available for this product.';
      return;
    }
    if (this.isUsed && line.hasOriginalBill && line.invoices.length === 0) {
      this.errorMessage = 'Upload this product bill image or PDF.';
      return;
    }
    if (this.isUsed && !line.hasOriginalBill && (!line.missingBillReason.trim() || line.verificationPhotos.length === 0)) {
      this.errorMessage = 'Enter the missing-bill reason and upload seller-with-this-product photo.';
      return;
    }
    if (this.isUsed && !line.isSellerProductOwner) {
      const ownerPhone = (line.productOwnerPhone ?? '').replace(/\D/g, '').slice(-10);
      if (!line.productOwnerName?.trim()) {
        this.errorMessage = 'Enter the actual product owner full name.';
        return;
      }
      if (ownerPhone.length !== 10) {
        this.errorMessage = 'Enter the actual product owner valid 10-digit mobile number.';
        return;
      }
      if (!line.productOwnerAddress?.trim()) {
        this.errorMessage = 'Enter the actual product owner complete address.';
        return;
      }
      if (line.productOwnerIdentityDocuments.length === 0) {
        this.errorMessage = 'Upload at least one actual product owner ID image or PDF.';
        return;
      }
      line.productOwnerPhone = ownerPhone;
    }
    if (this.isUsed) {
      line.quantity = 1;
      line.condition = 'Used';
      line.inlineSaved = true;
      line.productSearch = line.title.trim();
    }
    const duplicateIndex = this.lines.findIndex(
      (item, index) =>
        index !== this.editingLineIndex &&
        ((line.productId && item.productId === line.productId) ||
          (line.modelName.trim() &&
            item.modelName.trim().toLocaleLowerCase() === line.modelName.trim().toLocaleLowerCase())),
    );
    if (duplicateIndex >= 0) {
      this.errorMessage = 'This product or item code is already in the purchase. Edit its quantity instead.';
      return;
    }
    const savedLine = this.cloneLine(line);
    if (this.editingLineIndex === null) this.lines = [...this.lines, savedLine];
    else this.lines = this.lines.map((item, index) => (index === this.editingLineIndex ? savedLine : item));
    const wasEditing = this.editingLineIndex !== null;
    this.draftLine = this.emptyLine();
    this.editingLineIndex = null;
    this.showProductResults = false;
    this.successMessage = wasEditing ? 'Purchase item updated.' : 'Item added to this purchase.';
  }
  editPurchaseLine(index: number): void {
    this.draftLine = this.cloneLine(this.lines[index]);
    this.editingLineIndex = index;
    this.showProductResults = false;
    this.errorMessage = '';
  }
  cancelLineEdit(): void {
    this.draftLine = this.emptyLine();
    this.editingLineIndex = null;
    this.showProductResults = false;
  }
  evidenceChanged(kind: 'invoice' | 'identity' | 'verification' | 'ownerIdentity', event: Event): void {
    const input = event.target as HTMLInputElement;
    const target = kind === 'invoice'
      ? this.sellerInvoices
      : kind === 'identity'
        ? this.sellerIdentityDocuments
        : kind === 'ownerIdentity'
          ? this.productOwnerIdentityDocuments
          : this.sellerVerificationPhotos;
    const limit = kind === 'invoice' ? 8 : 4;
    const files = Array.from(input.files ?? []).slice(0, Math.max(0, limit - target.length));
    files.forEach((file) => target.push({ file, url: URL.createObjectURL(file), isPdf: file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') }));
    input.value = '';
  }
  removeEvidence(kind: 'invoice' | 'identity' | 'verification' | 'ownerIdentity', index: number): void {
    const target = kind === 'invoice'
      ? this.sellerInvoices
      : kind === 'identity'
        ? this.sellerIdentityDocuments
        : kind === 'ownerIdentity'
          ? this.productOwnerIdentityDocuments
          : this.sellerVerificationPhotos;
    if (target[index]?.url) URL.revokeObjectURL(target[index].url);
    target.splice(index, 1);
  }
  lineEvidenceChanged(line: PurchaseLine, kind: 'invoice' | 'verification' | 'ownerIdentity', event: Event): void {
    const input = event.target as HTMLInputElement;
    const target = kind === 'invoice' ? line.invoices : kind === 'ownerIdentity' ? line.productOwnerIdentityDocuments : line.verificationPhotos;
    const limit = kind === 'invoice' ? 8 : 4;
    Array.from(input.files ?? []).slice(0, Math.max(0, limit - target.length)).forEach((file) => {
      target.push({ file, url: URL.createObjectURL(file), isPdf: file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') });
    });
    input.value = '';
  }
  removeLineEvidence(line: PurchaseLine, kind: 'invoice' | 'verification' | 'ownerIdentity', index: number): void {
    const target = kind === 'invoice' ? line.invoices : kind === 'ownerIdentity' ? line.productOwnerIdentityDocuments : line.verificationPhotos;
    if (target[index]?.url) URL.revokeObjectURL(target[index].url);
    target.splice(index, 1);
  }
  usedOwnerPhoneInput(line: PurchaseLine, value: string): void {
    line.productOwnerPhone = value.replace(/\D/g, '').slice(0, 10);
  }
  productImagesChanged(e: Event): void {
    const input = e.target as HTMLInputElement;
    const remainingSlots = Math.max(0, 8 - this.productImages.length);
    const addedImages = Array.from(input.files ?? []).slice(0, remainingSlots);
    this.productImages.push(...addedImages);
    this.productImageUrls.push(...addedImages.map((file) => URL.createObjectURL(file)));
    input.value = '';
  }
  openImage(url: string, alt: string): void {
    this.fullImageUrl = url;
    this.fullImageAlt = alt;
  }
  closeImage(): void {
    this.fullImageUrl = '';
    this.fullImageAlt = '';
  }
  removeProductImage(index: number): void {
    if (this.productImageUrls[index]) URL.revokeObjectURL(this.productImageUrls[index]);
    this.productImages.splice(index, 1);
    this.productImageUrls.splice(index, 1);
  }
  lineThumbnailChanged(line: PurchaseLine, e: Event): void {
    if (line.thumbnailUrl) URL.revokeObjectURL(line.thumbnailUrl);
    line.thumbnail = (e.target as HTMLInputElement).files?.[0];
    line.thumbnailUrl = line.thumbnail ? URL.createObjectURL(line.thumbnail) : '';
  }
  lineGalleryChanged(line: PurchaseLine, e: Event): void {
    const input = e.target as HTMLInputElement;
    const remainingSlots = Math.max(0, 7 - line.galleryImages.length);
    const addedImages = Array.from(input.files ?? []).slice(0, remainingSlots);
    line.galleryImages.push(...addedImages);
    line.galleryUrls.push(...addedImages.map((file) => URL.createObjectURL(file)));
    input.value = '';
  }
  removeLineImage(line: PurchaseLine, type: 'thumbnail' | 'gallery', index = 0): void {
    if (type === 'thumbnail') {
      if (line.thumbnailUrl) URL.revokeObjectURL(line.thumbnailUrl);
      line.thumbnail = undefined;
      line.thumbnailUrl = '';
      return;
    }
    if (line.galleryUrls[index]) URL.revokeObjectURL(line.galleryUrls[index]);
    line.galleryImages.splice(index, 1);
    line.galleryUrls.splice(index, 1);
  }
  createSupplier(): void {
    if (!this.supplierForm.name.trim()) {
      this.supplierError = 'Supplier name is required.';
      return;
    }
    if (!/^\d{10}$/.test(this.supplierForm.phone)) {
      this.supplierError = 'Enter a valid 10-digit supplier mobile number.';
      return;
    }
    if (!this.supplierForm.address.trim()) {
      this.supplierError = 'Supplier complete address is required.';
      return;
    }
    this.isSavingSupplier = true;
    this.supplierError = '';
    this.supplierFeedback = '';
    this.orders.createSupplier(this.supplierForm).pipe(finalize(() => {
      this.isSavingSupplier = false;
      this.changeDetector.markForCheck();
    })).subscribe({
      next: (s) => {
        if (!s?.id || !s?.name) {
          this.supplierError = 'Supplier was saved, but its details could not be loaded. Refresh suppliers and try again.';
          this.loadSuppliers();
          return;
        }
        this.suppliers = [s, ...this.suppliers.filter((supplier) => supplier.id !== s.id)];
        this.selectSupplier(s);
        this.showNewSupplier = false;
        this.supplierFeedback = `${s.name} added and selected.`;
        this.supplierForm = {
          name: '',
          contactName: '',
          phone: '',
          email: '',
          taxNumber: '',
          address: '',
        };
        this.changeDetector.detectChanges();
      },
      error: (e) => {
        this.supplierError = httpErrorMessage(e, 'Unable to add supplier.');
        this.changeDetector.detectChanges();
      },
    });
  }
  toggleSupplierForm(): void {
    this.showNewSupplier = !this.showNewSupplier;
    this.supplierError = '';
    if (!this.showNewSupplier) {
      this.supplierForm = { name: '', contactName: '', phone: '', email: '', taxNumber: '', address: '' };
    }
  }
  supplierSearchChanged(): void {
    this.matchSupplier();
    this.showSupplierResults = true;
    this.supplierFeedback = '';
  }
  supplierPhoneInput(value:string):void { this.supplierForm.phone=value.replace(/\D/g,'').slice(0,10); }
  individualPhoneInput(value:string):void { this.individualPhone=value.replace(/\D/g,'').slice(0,10); }
  productOwnerPhoneInput(value:string):void { this.productOwnerPhone=value.replace(/\D/g,'').slice(0,10); }
  matchSupplier(): void {
    const search = this.supplierSearch.trim().toLocaleLowerCase();
    const supplier = this.suppliers.find(
      (item) => this.supplierLabel(item).toLocaleLowerCase() === search,
    );
    this.supplierId = supplier?.id ?? '';
  }
  selectSupplier(supplier: Supplier): void {
    this.supplierId = supplier.id;
    this.supplierSearch = this.supplierLabel(supplier);
    this.showSupplierResults = false;
    this.supplierError = '';
    this.sellerAddress = supplier.address ?? '';
  }
  supplierLabel(supplier: Supplier): string {
    return `${supplier.name}${supplier.phone ? ` · ${supplier.phone}` : ''}`;
  }
  lookupIndividual(): void {
    const phone = this.individualPhone.replace(/\D/g, '').slice(-10);
    if (phone.length !== 10 || this.aadhaarNumber.replace(/\D/g, '').length !== 12) {
      this.errorMessage = 'Enter valid 10-digit phone and 12-digit Aadhaar number.';
      return;
    }
    this.isVerifying = true;
    this.customers.lookupByPhone(phone).subscribe({
      next: (c) => {
        this.individualSeller = c;
        const existing = this.suppliers.find(
          (s) => s.phone?.replace(/\D/g, '').slice(-10) === phone,
        );
        if (!existing || !existing.address?.trim()) {
          this.isVerifying = false;
          this.errorMessage = 'This seller is not available with a complete address in the seller master. Add or update the seller first.';
          return;
        }
        this.sellerAddress = existing.address.trim();
        const continueVerification = (supplier: Supplier) => {
          this.supplierId = supplier.id;
          if (c.aadhaarVerificationStatus?.toUpperCase() === 'VERIFIED') {
            this.isVerifying = false;
            this.successMessage = 'Seller is already Aadhaar verified.';
            return;
          }
          this.customers
            .generateAadhaarOtp(c.identityUserId, this.aadhaarNumber)
            .pipe(finalize(() => (this.isVerifying = false)))
            .subscribe({
              next: (r) => {
                this.aadhaarReferenceId = r.referenceId;
                this.successMessage = r.message;
              },
              error: (e) =>
                (this.errorMessage = httpErrorMessage(e, 'Unable to send Aadhaar OTP.')),
            });
        };
        continueVerification(existing);
      },
      error: (e) => {
        this.isVerifying = false;
        this.errorMessage = httpErrorMessage(e, 'Registered seller not found.');
      },
    });
  }
  verifyIndividual(): void {
    if (!this.individualSeller || !this.aadhaarReferenceId || this.aadhaarOtp.length < 4) return;
    this.isVerifying = true;
    this.customers
      .verifyAadhaarOtp(
        this.individualSeller.identityUserId,
        this.aadhaarReferenceId,
        this.aadhaarOtp,
      )
      .pipe(finalize(() => (this.isVerifying = false)))
      .subscribe({
        next: (r) => {
          this.individualSeller = {
            ...this.individualSeller!,
            aadhaarVerificationStatus: 'VERIFIED',
            aadhaarVerifiedName: r.verifiedName,
          };
          this.successMessage = 'Individual seller Aadhaar verified.';
        },
        error: (e) => (this.errorMessage = httpErrorMessage(e, 'Unable to verify Aadhaar OTP.')),
      });
  }
  get individualVerified(): boolean {
    return this.individualSeller?.aadhaarVerificationStatus?.toUpperCase() === 'VERIFIED';
  }
  setBillAvailability(hasBill: boolean): void {
    this.hasOriginalBill = hasBill;
    if (hasBill) {
      this.sellerVerificationPhotos.forEach((item) => URL.revokeObjectURL(item.url));
      this.sellerVerificationPhotos = [];
      this.missingBillReason = '';
    } else {
      this.sellerInvoices.forEach((item) => URL.revokeObjectURL(item.url));
      this.sellerInvoices = [];
    }
  }
  changeLocation(): void {
    if (!this.stores.length) return;
    this.pendingStoreId = this.storeId;
    this.isLocationPickerOpen = true;
  }
  closeLocationPicker(): void {
    this.isLocationPickerOpen = false;
    this.pendingStoreId = '';
  }
  confirmLocationChange(): void {
    if (!this.pendingStoreId) return;
    this.storeId = this.pendingStoreId;
    this.loadFranchisePartners();
    this.locationMessage = `Assigned location selected: ${this.selectedStore?.name ?? ''}`;
    this.closeLocationPicker();
  }
  save(postImmediately = false): void {
    this.errorMessage = '';
    if (!this.purchaseType) {
      this.errorMessage = 'Select a purchase type.';
      return;
    }
    if (!this.supplierId) {
      this.errorMessage = this.isUsed ? 'Select and verify the seller.' : 'Select a supplier.';
      return;
    }
    if (!this.storeId) {
      this.errorMessage = 'Select the store location receiving this purchase.';
      return;
    }
    if (!this.isUsed && !this.sellerInvoiceNumber.trim()) {
      this.errorMessage = 'Enter the supplier invoice number.';
      return;
    }
    if (this.lines.length === 0) {
      this.errorMessage = 'Add at least one item to this purchase.';
      return;
    }
    if (this.franchiseAllocations.length) {
      if (this.totalFranchiseFunding > this.total) { this.errorMessage='Combined franchise funding cannot exceed the final purchase amount.'; return; }
      for (const allocation of this.franchiseAllocations) {
        const partner=this.franchisePartners.find(x=>x.id===allocation.franchisePartnerId); const amount=Number(allocation.amount)||0;
        if(!partner||amount<=0||amount>partner.available){this.errorMessage='Every franchise allocation must be positive and within its available balance.';return;}
      }
    }
    if (!this.companyFundEnabled && this.totalFranchiseFunding !== this.total) { this.errorMessage = 'Allocate the complete purchase amount using Company Fund and/or franchise funds.'; return; }
    if (this.businessFunding > this.companyFundBalance) { this.errorMessage = `Company inventory fund is insufficient. Available ${this.format(this.companyFundBalance)}; required ${this.format(this.businessFunding)}.`; return; }
    const incompleteLineIndex = this.lines.findIndex(
      (line) =>
        !line.title.trim() ||
        !Number(line.unitCost) ||
        Number(line.unitCost) <= 0 ||
        !Number(line.quantity) ||
        Number(line.quantity) <= 0,
    );
    if (incompleteLineIndex >= 0) {
      const line = this.lines[incompleteLineIndex];
      this.errorMessage = !line.title.trim()
        ? `Enter or select the product for item ${incompleteLineIndex + 1}.`
        : !Number(line.quantity) || Number(line.quantity) <= 0
          ? `Enter a valid quantity for item ${incompleteLineIndex + 1}.`
          : `Enter a valid purchase price for item ${incompleteLineIndex + 1}.`;
      return;
    }
    const duplicateLine = this.findDuplicateLine();
    if (duplicateLine) {
      this.errorMessage = duplicateLine;
      return;
    }
    if (!this.isUsed && this.lines.some((line) => !line.productId && !line.inlineSaved)) {
      this.errorMessage =
        'Select an existing New product or save the new product details for every row.';
      return;
    }
    const selectedSupplier = this.suppliers.find((supplier) => supplier.id === this.supplierId);
    const sellerName = this.purchaseType === 'IndividualUsed'
      ? `${this.individualSeller?.firstName ?? ''} ${this.individualSeller?.lastName ?? ''}`.trim()
      : selectedSupplier?.name?.trim() ?? '';
    const sellerPhone = (this.purchaseType === 'IndividualUsed' ? this.individualSeller?.phoneNumber : selectedSupplier?.phone)?.replace(/\D/g, '').slice(-10) ?? '';
    if (this.isUsed && (!sellerName || sellerPhone.length !== 10 || !this.sellerAddress.trim())) {
      this.errorMessage = 'Seller full name, 10-digit mobile number and complete address are required.';
      return;
    }
    if (this.isUsed && this.sellerIdentityDocuments.length === 0) {
      this.errorMessage = 'Upload seller ID proof (front/back image or PDF).';
      return;
    }
    if (this.isUsed) {
      for (let index = 0; index < this.lines.length; index += 1) {
        const line = this.lines[index];
        if (line.hasOriginalBill === null) { this.errorMessage = `Select bill availability for used product ${index + 1}.`; return; }
        if (line.hasOriginalBill && line.invoices.length === 0) { this.errorMessage = `Upload the original bill for used product ${index + 1}.`; return; }
        if (!line.hasOriginalBill && !line.missingBillReason.trim()) { this.errorMessage = `Enter the missing-bill reason for used product ${index + 1}.`; return; }
        if (!line.hasOriginalBill && line.verificationPhotos.length === 0) { this.errorMessage = `Upload seller-with-product photo for used product ${index + 1}.`; return; }
        if (!line.isSellerProductOwner) {
          if (!line.productOwnerName.trim()) { this.errorMessage = `Enter actual owner name for used product ${index + 1}.`; return; }
          if (line.productOwnerPhone.replace(/\D/g, '').length !== 10) { this.errorMessage = `Enter actual owner 10-digit mobile for used product ${index + 1}.`; return; }
          if (!line.productOwnerAddress.trim()) { this.errorMessage = `Enter actual owner address for used product ${index + 1}.`; return; }
          if (line.productOwnerIdentityDocuments.length === 0) { this.errorMessage = `Upload actual owner ID for used product ${index + 1}.`; return; }
        }
      }
    }
    if (this.purchaseType === 'IndividualUsed' && !this.individualVerified) {
      this.errorMessage = 'Verify the individual seller with Aadhaar.';
      return;
    }
    const usedItemWithoutImage = this.isUsed
      ? this.lines.findIndex((line) => line.galleryImages.length === 0 && !line.thumbnail)
      : -1;
    if (usedItemWithoutImage >= 0) {
      this.errorMessage = `Add at least one clear image for used product ${usedItemWithoutImage + 1}.`;
      return;
    }
    const d = new FormData();
    d.append('purchaseType', this.purchaseType);
    d.append('supplierId', this.supplierId);
    d.append('storeId', this.storeId);
    d.append('companyFundedAmount', String(this.businessFunding));
    if (this.franchiseAllocations.length) d.append('franchiseAllocationsJson', JSON.stringify(this.franchiseAllocations.map(x=>({franchisePartnerId:x.franchisePartnerId,amount:Number(x.amount)||0}))));
    if (this.individualSeller)
      d.append('individualSellerUserId', this.individualSeller.identityUserId);
    if (this.isUsed) {
      d.append('sellerName', sellerName);
      d.append('sellerPhone', sellerPhone);
      d.append('sellerAddress', this.sellerAddress.trim());
    }
    d.append('sellerInvoiceNumber', this.sellerInvoiceNumber.trim());
    d.append('notes', this.notes.trim());
    d.append('discountAmount', String(Number(this.discountAmount) || 0));
    d.append('taxAmount', String(Number(this.taxAmount) || 0));
    d.append('transportAmount', String(Number(this.transportAmount) || 0));
    d.append('otherCharges', String(Number(this.otherCharges) || 0));
    d.append('amountPaid', String(Number(this.amountPaid) || 0));
    d.append('paymentMethod', this.paymentMethod);
    d.append('postImmediately', String(postImmediately));
    d.append(
      'itemsJson',
      JSON.stringify(
        this.lines.map((x) => ({
          productId: x.productId,
          categoryId: x.categoryId,
          brandId: x.brandId,
          title: x.title,
          description: x.description,
          modelName: x.modelName,
          identifierType: x.identifierType || undefined,
          identifierValue: x.identifierValue.trim() || undefined,
          condition: this.isUsed ? 'Used' : 'New',
          quantity: this.isUsed ? 1 : Number(x.quantity),
          unitCost: Number(x.unitCost),
          repairCost: Number(x.repairCost) || 0,
          extraCost: Number(x.extraCost) || 0,
          hasOriginalBill: x.hasOriginalBill,
          invoiceNumber: x.invoiceNumber,
          missingBillReason: x.missingBillReason,
          isSellerProductOwner: x.isSellerProductOwner,
          productOwnerName: x.productOwnerName,
          productOwnerPhone: x.productOwnerPhone,
          productOwnerAddress: x.productOwnerAddress,
        })),
      ),
    );
    this.sellerInvoices.forEach((item) => d.append('sellerInvoices', item.file, item.file.name));
    this.sellerIdentityDocuments.forEach((item) => d.append('sellerIdentityDocuments', item.file, item.file.name));
    this.sellerVerificationPhotos.forEach((item) => d.append('sellerVerificationImages', item.file, item.file.name));
    if (!this.isSellerProductOwner) {
      this.productOwnerIdentityDocuments.forEach((item) => d.append('productOwnerIdentityDocuments', item.file, item.file.name));
    }
    this.lines.forEach((line, index) => {
      if (line.thumbnail)
        d.append(`productThumbnail_${index}`, line.thumbnail, line.thumbnail.name);
      line.galleryImages.forEach((image) => d.append(`productGallery_${index}`, image, image.name));
      line.invoices.forEach((item) => d.append(`itemInvoice_${index}`, item.file, item.file.name));
      line.verificationPhotos.forEach((item) => d.append(`itemVerification_${index}`, item.file, item.file.name));
      line.productOwnerIdentityDocuments.forEach((item) => d.append(`itemOwnerIdentity_${index}`, item.file, item.file.name));
    });
    this.isSaving = true;
    this.errorMessage = '';
    this.orders
      .createPurchase(d)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.changeDetector.markForCheck();
        }),
      )
      .subscribe({
        next: (r) => {
          this.isSaving = false;
          this.completedVoucherUrl = r.voucher?.url ?? '';
          this.successMessage = r.warning
            ? `Purchase ${r.purchase?.voucherNumber ?? ''} saved. ${r.warning}`
            : `Purchase ${r.purchase?.voucherNumber ?? ''} completed.`;
          this.refreshProducts();
          this.changeDetector.markForCheck();
          setTimeout(() => document.getElementById('purchase-complete')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
        },
        error: (e) => {
          this.isSaving = false;
          this.errorMessage = httpErrorMessage(e, 'Unable to complete purchase.');
          this.changeDetector.markForCheck();
        },
      });
  }
  format(v: number): string {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v || 0);
  }
  private emptyLine(): PurchaseLine {
    return {
      title: '',
      productSearch: '',
      isCreatingNew: false,
      inlineSaved: false,
      description: '',
      modelName: '',
      identifierType: '',
      identifierValue: '',
      condition: this.isUsed ? 'Used' : 'New',
      quantity: this.isUsed ? 1 : null,
      unitCost: null,
      repairCost: null,
      extraCost: null,
      thumbnailUrl: '',
      galleryImages: [],
      galleryUrls: [],
      hasOriginalBill: null,
      invoiceNumber: '',
      invoices: [],
      verificationPhotos: [],
      missingBillReason: '',
      isSellerProductOwner: false,
      productOwnerName: '',
      productOwnerPhone: '',
      productOwnerAddress: '',
      productOwnerIdentityDocuments: [],
    };
  }
  private cloneLine(line: PurchaseLine): PurchaseLine {
    return {
      ...line,
      galleryImages: [...line.galleryImages],
      galleryUrls: [...line.galleryUrls],
      invoices: [...line.invoices],
      verificationPhotos: [...line.verificationPhotos],
      productOwnerIdentityDocuments: [...line.productOwnerIdentityDocuments],
    };
  }
  private revokePreviews(): void {
    [...this.sellerInvoices, ...this.sellerIdentityDocuments, ...this.sellerVerificationPhotos, ...this.productOwnerIdentityDocuments].forEach((item) => URL.revokeObjectURL(item.url));
    this.productImageUrls.forEach((url) => URL.revokeObjectURL(url));
    this.lines.forEach((line) => {
      if (line.thumbnailUrl) URL.revokeObjectURL(line.thumbnailUrl);
      line.galleryUrls.forEach((url) => URL.revokeObjectURL(url));
      [...line.invoices, ...line.verificationPhotos, ...line.productOwnerIdentityDocuments].forEach((item) => URL.revokeObjectURL(item.url));
    });
    if (this.draftLine.thumbnailUrl) URL.revokeObjectURL(this.draftLine.thumbnailUrl);
    this.draftLine.galleryUrls.forEach((url) => URL.revokeObjectURL(url));
    [...this.draftLine.invoices, ...this.draftLine.verificationPhotos, ...this.draftLine.productOwnerIdentityDocuments].forEach((item) => URL.revokeObjectURL(item.url));
  }
  private refreshProducts(): void {
    this.loadSearchableProducts().subscribe({
      next: (products) => (this.products = products),
      error: () => {
        // Purchase completion remains successful even if refreshing the search list fails.
      },
    });
  }
  loadSuppliers(): void {
    this.isLoadingSuppliers = true;
    this.orders
      .suppliers()
      .pipe(finalize(() => {
        this.isLoadingSuppliers = false;
        this.changeDetector.detectChanges();
      }))
      .subscribe({
        next: (suppliers) => {
          this.suppliers = suppliers;
          if (this.supplierId) {
            const selected = suppliers.find((supplier) => supplier.id === this.supplierId);
            if (selected) this.supplierSearch = this.supplierLabel(selected);
          }
          this.changeDetector.detectChanges();
        },
        error: (e) => (this.errorMessage = httpErrorMessage(e, 'Unable to load sellers.')),
      });
  }
  private rememberPurchaseItem(line: PurchaseLine): void {
    const key = `${line.title.trim()}|${line.modelName.trim()}`.toLocaleLowerCase();
    const saved = this.cloneLine(line);
    const existingIndex = this.savedPurchaseItems.findIndex(
      (item) => `${item.title.trim()}|${item.modelName.trim()}`.toLocaleLowerCase() === key,
    );
    this.savedPurchaseItems = existingIndex < 0
      ? [saved, ...this.savedPurchaseItems]
      : this.savedPurchaseItems.map((item, index) => (index === existingIndex ? saved : item));
  }
  private loadSearchableProducts(): Observable<Product[]> {
    return forkJoin({
      owned: this.productsService.listBySeller(),
      catalogue: this.productsService.list({ listingType: 'New', pageNumber: 1, pageSize: 100 }),
    }).pipe(
      map(({ owned, catalogue }) => {
        const products = new Map<string, Product>();
        [...owned, ...catalogue].forEach((product) => products.set(product.id, product));
        return [...products.values()];
      }),
    );
  }
  private findDuplicateLine(): string | null {
    const productIds = new Set<string>();
    const itemCodes = new Set<string>();
    for (const [index, line] of this.lines.entries()) {
      if (line.productId) {
        if (productIds.has(line.productId))
          return `Product in item ${index + 1} is already added. Increase its quantity instead.`;
        productIds.add(line.productId);
      }
      const itemCode = line.modelName.trim().toLocaleLowerCase();
      if (itemCode) {
        if (itemCodes.has(itemCode))
          return `Item code / SKU “${line.modelName.trim()}” is already used in this purchase.`;
        itemCodes.add(itemCode);
      }
    }
    return null;
  }
  private loadLocations(): void {
    this.business.stores(this.locationScope).subscribe({
      next: (stores) => {
        this.stores = stores;
        const selected =
          stores.find((x) => x.id === this.storeId) ?? stores.find((x) => x.isPrimary) ?? stores[0];
        this.storeId = selected?.id ?? '';
        this.loadFranchisePartners();
        this.locationMessage = selected
          ? `Assigned location selected: ${selected.name}`
          : 'No permitted store location is assigned to your account.';
      },
      error: (e) => {
        this.stores = [];
        this.storeId = '';
        this.errorMessage = httpErrorMessage(e, 'Unable to load assigned store locations.');
      },
    });
  }
  private loadFranchisePartners(): void {
    if (!this.storeId) { this.franchisePartners=[]; this.franchiseAllocations=[]; this.companyFundBalance=0; return; }
    this.finance.companyFund(this.storeId).subscribe({next:data=>{this.companyFundBalance=Number(data.accounts[0]?.balance??0);this.changeDetector.markForCheck();},error:()=>{this.companyFundBalance=0;this.changeDetector.markForCheck();}});
    this.finance.franchisePartners(this.storeId).subscribe({next:items=>{
      this.franchisePartners=items.filter(item=>item.status!=='Paid' && item.available>0);
      this.franchiseAllocations=this.franchiseAllocations.filter(x=>this.franchisePartners.some(item=>item.id===x.franchisePartnerId));
      this.changeDetector.markForCheck();
    },error:()=>{this.franchisePartners=[];this.franchiseAllocations=[];}});
  }
  addFranchiseFund(partnerId:string):void { if(!partnerId||this.franchiseAllocations.some(x=>x.franchisePartnerId===partnerId))return;this.franchiseAllocations=[...this.franchiseAllocations,{franchisePartnerId:partnerId,amount:null,percentage:null}]; }
  removeFranchiseFund(index:number):void { this.franchiseAllocations=this.franchiseAllocations.filter((_,i)=>i!==index); }
  franchiseName(id:string):string { return this.franchisePartners.find(x=>x.id===id)?.name??'Franchise partner'; }
  franchiseAvailable(id:string):number { return Number(this.franchisePartners.find(x=>x.id===id)?.available??0); }
  allocationAmountChanged(row:{amount:number|null;percentage:number|null}):void { row.amount=Math.max(0,Number(row.amount)||0);row.percentage=this.total>0?Number(((row.amount/this.total)*100).toFixed(2)):0; }
  allocationPercentageChanged(row:{amount:number|null;percentage:number|null}):void { row.percentage=Math.max(0,Math.min(100,Number(row.percentage)||0));row.amount=Number((this.total*row.percentage/100).toFixed(2)); }
}
